import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide,
  Switch
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  chromosomeRegion,
  formatScientificToDecimal,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  switchACMG,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent?: any
  setOpen?: any
  setHistoryOpen?: any
  setCommentsOpen?: any
  setTranscript_list?: any
  handleMenuClick?: any
  getColumnSearchProps?: any
  setFilterGeneOpen?: any
  setMenuType?: any
  setFilterGeneData?: any
  getFilterGene?: any
  setIndexOpen?: any
  setGeneIndexOpen?: any
  setGeneOpen?: any
  setAcmgOpen?: any
  setOMIMOpen?: any
  VariationGeneRef?: any
  analysisInfo?: any
  query?: any
  indexChecked?: boolean
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const Columns1 = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const isPipelineVersion = props.analysisInfo?.pipelineVersion

  // 辅助函数：获取背景色
  const getBgColor = (row: any) => {
    let bgcolor = color.find(
      (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
    )
      ? (color.find((i) => i.key === row.original.CLINSIG_final?.toLowerCase())
          ?.color as any)
      : 'rgba(0, 0, 0)'

    if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
      //
    } else if (
      row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
    ) {
      //
    } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

    return bgcolor
  }

  // 辅助函数：获取基因名称
  const getGeneName = (row: any) => {
    if (props.query?.title === t('MT')) {
      const genes = isPipelineVersion ? row.genes : row.MT_genes
      return genes && Array.isArray(genes) ? genes.join(';') : row.gene
    }
    return row.gene
  }

  // 辅助函数：获取转录本ID
  const getTranscriptId = (row: any) => {
    return isPipelineVersion ? row.feature : row.transcript
  }

  // 辅助函数：获取MANE标签
  const getManeLabel = (row: any) => {
    return isPipelineVersion ? row.mane ?? 'MANE' : 'MANE'
  }

  // 辅助函数：获取rsID
  const getRsId = (row: any) => {
    return isPipelineVersion ? row.rsid : row.rs_id
  }

  // 辅助函数：获取功能影响
  const getConsequence = (row: any) => {
    return isPipelineVersion ? row.consequence : row.coding_effect
  }

  // 辅助函数：获取变异位置
  const getVarLocation = (row: any) => {
    return isPipelineVersion
      ? row.variant_locations?.join(';')
      : row.var_location
  }

  // 辅助函数：获取cDNA变化
  const getHgvsc = (row: any) => {
    return isPipelineVersion ? row.hgvsc : row.c_nomen
  }

  // 辅助函数：获取蛋白质变化
  const getHgvsp = (row: any) => {
    return isPipelineVersion ? row.hgvsp : row.p_nomen
  }

  // 辅助函数：获取等位基因频率
  const getAlleleBalance = (row: any) => {
    return isPipelineVersion ? row.allele_balance : row.allele_frequency
  }

  // 辅助函数：获取覆盖度
  const getCoverage = (row: any) => {
    return isPipelineVersion ? row.ad : row.coverage
  }

  // 辅助函数：获取质量评分
  const getQual = (row: any) => {
    return isPipelineVersion ? row.qual : row.QUAL
  }

  // 辅助函数：获取SpliceAI最大评分
  const getSpliceAI = (row: any) => {
    return isPipelineVersion ? row.spliceai_max_score : row.SpliceAI_max_score
  }

  // 辅助函数：获取质量评分
  const getRevel = (row: any) => {
    return isPipelineVersion ? row.revel : row.Revel_score
  }

  // 辅助函数：获取质量评分
  const getAlphaM = (row: any) => {
    return isPipelineVersion ? row.am_pathogenicity : row.AM_pathogenicity
  }

  // 辅助函数：获取ClinVar变异ID
  const getClinvarVariantId = (row: any) => {
    return isPipelineVersion ? row.clinvar_id : row.CLINVAR_variant_id
  }

  // 辅助函数：获取ClinVar分类
  const getClinvarClassification = (row: any) => {
    return isPipelineVersion ? row.clinvar_clinsig_final : row.CLINSIG_final
  }

  // 辅助函数：获取ClinVar评分
  const getClinvarScore = (row: any) => {
    return isPipelineVersion ? row.clinvar_gold_stars : row.CLINVAR_gold_stars
  }

  // 辅助函数：获取Index数据
  const getIndexData = (row: any) => {
    return isPipelineVersion ? row.tempGlsIndex : row.Index
  }

  // 辅助函数：获取ACMG评分
  const getAcmgScore = (row: any) => {
    return isPipelineVersion ? row.acmg_score : row.ACMG_score
  }

  // 辅助函数：格式化ACMG分类
  const formatACMG = (classification: string) => {
    if (!classification) return ''
    return classification
      .split(' ')
      .map((word: string) => {
        if (word.includes('Uncertain')) return `VU`
        if (word.includes('null')) return ``
        else return word[0] && word[0].toUpperCase()
      })
      .join('')
  }

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    let column: ColumnDef<Variation>[] = [
      {
        accessorKey: 'gene',
        id: 'gene',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Gene')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('TranscriptID')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          const bgcolor = getBgColor(row)
          const badges = row.original.user_variant_comments
            ? row.original.user_variant_comments.comments.length
            : 0
          const hisBadges = row.original.tempHistory
            ? row.original.tempHistory.length
            : 0
          const transcriptId = getTranscriptId(row.original)
          const maneLabel = getManeLabel(row.original)
          const NM = `${transcriptId}(${maneLabel})`

          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              direction="column"
              alignItems="start"
            >
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent="space-between"
                width={'100%'}
              >
                {ability && ability.can('read', 'report-search') ? (
                  <Badges
                    onClick={(e) => {
                      e.stopPropagation()
                      props.setHistoryOpen(true)
                      props.setCurrent(row.original)
                    }}
                    badgeContent={hisBadges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': {
                        top: '6px',
                        right: '-45px'
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <Badges
                      onClick={(e) => {
                        e.stopPropagation()
                        props.setCommentsOpen(true)
                        props.setCurrent(row.original)
                      }}
                      showZero
                      badgeContent={badges}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        color: 'inherit',
                        '& .MuiBadge-badge': {
                          top: '6px',
                          right: '-18px'
                        }
                      }}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      <Tooltip title={getGeneName(row.original)}>
                        <TypographyA
                          sx={{
                            color:
                              bgcolor === 'rgba(229,38,35,0.8)'
                                ? 'white'
                                : bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgb(109,56,149,0.8)'
                                ? 'black'
                                : '#23527c',
                            fontWeight: '700',
                            cursor: 'pointer',
                            maxWidth: 80,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {getGeneName(row.original)}
                        </TypographyA>
                      </Tooltip>
                    </Badges>
                  </Badges>
                ) : (
                  <Badges
                    onClick={(e) => {
                      e.stopPropagation()
                      props.setCommentsOpen(true)
                      props.setCurrent(row.original)
                    }}
                    showZero
                    badgeContent={badges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': {
                        top: '6px',
                        right: '-18px'
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <Tooltip title={getGeneName(row.original)}>
                      <TypographyA
                        sx={{
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          fontWeight: '700',
                          cursor: 'pointer',
                          maxWidth: 80,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {getGeneName(row.original)}
                      </TypographyA>
                    </Tooltip>
                  </Badges>
                )}

                {row && row.getCanExpand && row.getCanExpand() ? (
                  <button
                    {...{
                      onClick: row.getToggleExpandedHandler(),
                      style: { cursor: 'pointer' }
                    }}
                  >
                    {row.getIsExpanded() ? (
                      <IconButton
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: 'black'
                          },
                          padding: 0
                        }}
                      >
                        <ChevronDown />
                      </IconButton>
                    ) : (
                      <IconButton
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: 'black'
                          },
                          padding: 0
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    )}
                  </button>
                ) : null}
              </Stack>

              <Stack
                sx={{
                  position: 'relative'
                }}
                maxWidth={'100%'}
                direction="row"
                alignItems="center"
              >
                <Tooltip
                  title={NM}
                  placement={'top-start'}
                  disableHoverListener={NM.length <= 14}
                >
                  <TypographyA
                    id="qw"
                    sx={{
                      width: '100%',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : bgcolor === 'rgb(107,148,200,0.8)' ||
                            bgcolor === 'rgb(109,56,149,0.8)'
                          ? 'black'
                          : '#23527c',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.nuccore}/${transcriptId}`
                      )
                    }}
                  >
                    {NM.length >= 14 ? `${NM}` : NM}
                  </TypographyA>
                </Tooltip>
              </Stack>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'dbSNP',
        id: 'dbSNP',
        header: () => (
          <Stack maxWidth="100%">
            <Stack maxWidth="100%">
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Position')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('dbSNP')}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const bgcolor = getBgColor(row)
          const rsId = getRsId(row.original)

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <TypographyA
                sx={{
                  cursor: 'pointer',
                  color:
                    bgcolor === 'rgba(229,38,35,0.8)'
                      ? 'white'
                      : bgcolor === 'rgb(107,148,200,0.8)' ||
                        bgcolor === 'rgb(109,56,149,0.8)'
                      ? 'black'
                      : '#23527c',
                  fontWeight: '700'
                }}
                onClick={(e) => {
                  if (
                    (props.analysisInfo && props.analysisInfo.bamFile) ||
                    props.analysisInfo.sampleType === 'fastq'
                  ) {
                    props.handleMenuClick(e, 'IGV')
                    props.setCurrent && props.setCurrent(row.original)
                  } else if (
                    props.analysisInfo.analysisType === 'family_analysis' ||
                    props.analysisInfo.analysisType === 'big_family' ||
                    props.analysisInfo.analysisType === 'carrier'
                  ) {
                    const fqSamples = props.analysisInfo.samples?.filter(
                      (item: any) => item && item.sampleType === 'fastq'
                    )

                    if (fqSamples && fqSamples.length) {
                      props.handleMenuClick(e, 'IGV')
                      props.setCurrent && props.setCurrent(row.original)
                    } else {
                      window.open(
                        `${ExternalLinks.genome}?db=${
                          props.analysisInfo.genomeBuild ?? 'hg19'
                        }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                          row.original.chrom
                        }:${row.original.pos}-${row.original.pos}`
                      )
                    }
                  } else
                    window.open(
                      `${ExternalLinks.genome}?db=${
                        props.analysisInfo.genomeBuild ?? 'hg19'
                      }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                        row.original.chrom
                      }:${row.original.pos}-${row.original.pos}`
                    )
                }}
              >
                {`${row.original.chrom}:${row.original.pos}`}
              </TypographyA>

              <Tooltip
                disableHoverListener={rsId ? rsId?.length <= 14 : true}
                title={rsId}
                placement={'top-start'}
              >
                <TypographyA
                  sx={{
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)'
                        ? 'white'
                        : bgcolor === 'rgb(107,148,200,0.8)' ||
                          bgcolor === 'rgb(109,56,149,0.8)'
                        ? 'black'
                        : '#23527c',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontWeight: '700'
                  }}
                  onClick={() => {
                    window.open(
                      `${ExternalLinks.ncbi.snp_ref}?type=rs&rs=${rsId}`
                    )
                  }}
                >
                  {`${rsId ?? ''}`}
                </TypographyA>
              </Tooltip>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'coding_effect',
        id: 'coding_effect',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Function')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {`${t('Location')}`}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column" spacing={1}>
            <Tooltip
              disableHoverListener={
                getConsequence(row.original)
                  ? getConsequence(row.original)?.length <= 14
                  : true
              }
              placement={'top-start'}
              title={getConsequence(row.original)}
            >
              <TypographyA
                sx={{
                  maxWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >{`${getConsequence(row.original)}`}</TypographyA>
            </Tooltip>

            <Tooltip
              disableHoverListener={
                getVarLocation(row.original)
                  ? getVarLocation(row.original)?.length +
                      row.original.exon?.length <=
                    14
                  : true
              }
              placement={'top-start'}
              title={`${getVarLocation(row.original)}${
                row.original.exon === '.' || !row.original.exon
                  ? ''
                  : `(${row.original.exon})`
              }`}
            >
              <TypographyA
                sx={{
                  maxWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >{`${getVarLocation(row.original)}${
                row.original.exon === '.' || !row.original.exon
                  ? ''
                  : `(${row.original.exon})`
              }`}</TypographyA>
            </Tooltip>
          </Stack>
        )
      },
      {
        accessorKey: 'nuc_change',
        id: 'nuc_change',
        enableSorting: false,
        header: () => (
          <React.Fragment>
            <Stack maxWidth="100%">
              <span style={{ textAlign: 'start' }}>{t('Base change')}</span>
            </Stack>
          </React.Fragment>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const sampleList = props.analysisInfo.samples ?? []
          const flag =
            sampleList?.some(
              (sample: any) =>
                (sample.cnvBatchTaskId &&
                  sample.cnvBatchStatus === 'processed') ||
                sample.hasExomeDepthCnv
            ) && row.original.CNV

          return (
            <Stack
              maxWidth={'100%'}
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems={'center'}
            >
              <Box>
                <Stack
                  direction="row"
                  alignItems={'center'}
                  justifyContent="start"
                  width={'100%'}
                >
                  <Tooltip
                    disableHoverListener={
                      `${row.original.ref}>${row.original.alt}`?.length <= 14
                    }
                    title={`${row.original.ref}>${row.original.alt}`}
                    placement="top-start"
                  >
                    <TypographyA
                      sx={{
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: '#23527c',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                      onClick={(e) => {
                        props.setCurrent && props.setCurrent(row.original)
                        props.setFilterGeneOpen(true)
                        props.setMenuType('filter_gene')
                        props.setFilterGeneData([])
                        props.getFilterGene.mutateAsync(row.original.gene)
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                    >
                      {`${row.original.ref}>${row.original.alt}`}
                    </TypographyA>
                  </Tooltip>

                  {flag ? (
                    <TypographyA
                      sx={{
                        marginLeft: '15px',
                        color: 'red !important'
                      }}
                    >
                      cnv
                    </TypographyA>
                  ) : (
                    ''
                  )}
                </Stack>

                <Tooltip
                  disableHoverListener={
                    getHgvsc(row.original)
                      ? getHgvsc(row.original)?.length <= 14
                      : true
                  }
                  title={`${getHgvsc(row.original)}`}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: 'left'
                    }}
                  >
                    {`${getHgvsc(row.original) ?? ''}`}
                  </TypographyA>
                </Tooltip>

                <Tooltip
                  disableHoverListener={
                    getHgvsp(row.original)
                      ? getHgvsp(row.original)?.length <= 14
                      : true
                  }
                  title={`${getHgvsp(row.original)}`}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: 'left'
                    }}
                  >
                    {`${getHgvsp(row.original) ?? ''}`}
                  </TypographyA>
                </Tooltip>
              </Box>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'ad',
        id: 'ad',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Tooltip
              placement="top-start"
              title={`实心圆表纯合，半实心表杂合；ref/alt；新发变异紫色加粗`}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('Zygosity')}/${t('Coverage')}`}
                </span>

                {type === 'sample' ? (
                  <React.Fragment>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('QUAL')}
                    </span>
                  </React.Fragment>
                ) : (
                  ''
                )}
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const bgcolor = getBgColor(row)
          const alleleBalance = getAlleleBalance(row.original)
          const coverage = getCoverage(row.original)
          const qual = getQual(row.original)

          const group_data = row.original.group_data
            ? row.original.group_data
                .map((item: any) => {
                  const temp = renameChildTypes(
                    props.analysisInfo.samples
                  ).find((items: any) => items.id == item.sample_id)
                  const type = temp ? temp.type : ''

                  return {
                    ...item,
                    type: type,
                    typeIndex:
                      type === 'affected_child'
                        ? 0
                        : type === 'mother'
                        ? 2
                        : type === 'father'
                        ? 1
                        : type === 'child'
                        ? 0
                        : 0
                  }
                })
                .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
            : []

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              {type === 'sample' ? (
                <React.Fragment>
                  {(alleleBalance || alleleBalance === 0) &&
                  alleleBalance !== '.' ? (
                    <Stack direction={'row'} alignItems="center" spacing={1}>
                      <Stack maxWidth={'100%'} direction="column" spacing={1}>
                        {alleleBalance < 0.85 ? (
                          <CircleHalfFull
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        ) : (
                          <Circle
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        )}
                      </Stack>

                      <TypographyA
                        sx={{
                          maxWidth: '100%'
                        }}
                      >
                        {`${coverage}  [${alleleBalance?.toFixed(2)}] `}
                      </TypographyA>
                    </Stack>
                  ) : (
                    ''
                  )}

                  <TypographyA
                    sx={{
                      maxWidth: '100%',
                      textAlign: 'left'
                    }}
                  >
                    {(qual as unknown as string) !== '.' || qual
                      ? `${qual?.toFixed(1)}`
                      : ''}
                  </TypographyA>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {group_data.map((item: any) => {
                    if (item && item.allele_balance && item.ad) {
                      const temp = item
                      const mother = group_data.find(
                        (i: any) => i.type === 'mother'
                      )
                      const father = group_data.find(
                        (i: any) => i.type === 'father'
                      )

                      return (
                        <Tooltip
                          title={
                            temp
                              ? temp.type.includes('//')
                                ? `${t(temp.type.split('//')[0])}${
                                    temp.type.split('//')[1]
                                  }`
                                : t(temp.type)
                              : ''
                          }
                          placement="top-end"
                        >
                          <Stack
                            direction={'row'}
                            alignItems="center"
                            key={item.sample_id}
                            spacing={1}
                          >
                            <Stack
                              maxWidth={'100%'}
                              direction="column"
                              spacing={1}
                            >
                              {item.allele_balance < 0.85 ? (
                                <CircleHalfFull
                                  sx={{
                                    color: 'black',
                                    fontSize: '0.875rem'
                                  }}
                                />
                              ) : (
                                <Circle
                                  sx={{
                                    color: 'black',
                                    fontSize: '0.875rem'
                                  }}
                                />
                              )}
                            </Stack>

                            <TypographyA
                              sx={{
                                maxWidth: '100%',
                                color:
                                  !mother?.allele_balance &&
                                  !father?.allele_balance
                                    ? 'rgb(243,27,243)'
                                    : 'inherit',
                                fontWeight:
                                  !mother?.allele_balance &&
                                  !father?.allele_balance
                                    ? 'bold'
                                    : 'inherit'
                              }}
                            >
                              {`${item.ad} [${item.allele_balance.toFixed(
                                2
                              )}] (${
                                temp ? judgeType(temp.type, group_data) : ''
                              })`}
                            </TypographyA>
                          </Stack>
                        </Tooltip>
                      )
                    }
                  })}
                </React.Fragment>
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'index',
        id: 'index',
        enableSorting: false,
        header: () => (
          <Tooltip
            title={
              <>
                <p>用户下有该基因的样本量/用户有该基因的样本量</p>

                <p>组织下有该基因的样本量/组织有该基因的样本量</p>

                <p>平台下有该基因的样本量/平台有该基因的样本量</p>
              </>
            }
          >
            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (User)')}
                  </span>
                </React.Fragment>
              </Stack>

              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (Organization)')}
                  </span>
                </React.Fragment>
              </Stack>

              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (GLS)')}
                  </span>
                </React.Fragment>
              </Stack>
            </Stack>
          </Tooltip>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const tempGlsIndex = getIndexData(row.original)
          const bgcolor = getBgColor(row)

          return (
            <Stack
              onClick={() => {
                props.setCurrent(row.original)
                props.setIndexOpen(true)
              }}
              maxWidth={'100%'}
              direction="column"
              spacing={1}
            >
              {tempGlsIndex ? (
                <React.Fragment>
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    {isPipelineVersion
                      ? `${tempGlsIndex.user_index ?? 0} / ${
                          tempGlsIndex.user ?? 0
                        }`
                      : `${tempGlsIndex.accountTotal ?? 0} / ${
                          tempGlsIndex.platformTotal ?? 0
                        }`}
                  </TypographyA>

                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    {isPipelineVersion
                      ? `${tempGlsIndex.organization_index ?? 0} / ${
                          tempGlsIndex.organization ?? 0
                        }`
                      : `${tempGlsIndex.organizationTotal ?? 0} / ${
                          tempGlsIndex.organizationTotalSamples ?? 0
                        }`}
                  </TypographyA>

                  {isPipelineVersion && (
                    <TypographyA
                      sx={{
                        width: '100%',
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : '#23527c'
                      }}
                    >
                      {`${tempGlsIndex.platform_index ?? 0} / ${
                        tempGlsIndex.platform ?? 0
                      }`}
                    </TypographyA>
                  )}
                </React.Fragment>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'articles',
        id: 'articles',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <React.Fragment>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Articles')}
                </span>
              </React.Fragment>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const LitVarArticles = row.original.LitVarArticles
          const Articles = row.original.Articles
          const bgcolor = getBgColor(row)

          return (
            <Stack
              onClick={(e) => {
                e.stopPropagation()
                props.setAcmgOpen(true)
                props.setCurrent(row.original)
                setTimeout(() => {
                  props.VariationGeneRef.current &&
                    props.VariationGeneRef.current.handleClickLeft &&
                    props.VariationGeneRef.current.handleClickLeft(
                      3,
                      'Publication'
                    )
                }, 2000)
              }}
              maxWidth={'100%'}
              direction="column"
              spacing={1}
              sx={{
                cursor: 'pointer'
              }}
            >
              {LitVarArticles?.count ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  LitVar:{LitVarArticles?.count ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.variantPubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  Clinvar:{Articles?.variantPubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {isPipelineVersion && Articles?.genePubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  OMIM:{Articles?.genePubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'gnomAD_exome_ALL',
        id: 'gnomAD_exome_ALL',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'} justifyContent="center">
              {props.query?.title === t('MT') ? (
                <>
                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`gAD_hom_all`}</span>

                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`gAD_het_all`}</span>
                </>
              ) : (
                <>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`GnomAD Exome ALL`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`GnomAD Genome ALL`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('WBBC')}`}
                  </span>
                </>
              )}
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column">
            {props.query?.title === t('MT') ? (
              <>
                <TypographyA sx={{ maxWidth: '100%' }}>
                  {row.original.AF_hom === ('.' as unknown as string)
                    ? '.'
                    : Number(row.original.AF_hom) === 0
                    ? 0
                    : `${Number(row.original.AF_hom)?.toFixed(8)}`}
                </TypographyA>

                <TypographyA sx={{ maxWidth: '100%' }}>
                  {(row.original.AF_het as unknown as string) === '.'
                    ? '.'
                    : Number(row.original.AF_het) === 0
                    ? 0
                    : `${Number(row.original.AF_het)?.toFixed(8)}`}
                </TypographyA>
              </>
            ) : (
              <>
                <TypographyA sx={{ maxWidth: '100%' }}>
                  {(isPipelineVersion
                    ? row.original.gnomade_af
                    : (row.original.gnomAD_exome_ALL as unknown as string)) ===
                  '.'
                    ? '.'
                    : (isPipelineVersion
                        ? row.original.gnomade_af
                        : row.original.gnomAD_exome_ALL) === 0
                    ? 0
                    : `${formatScientificToDecimal(
                        isPipelineVersion
                          ? row.original.gnomade_af
                          : row.original.gnomAD_exome_ALL
                      )}`}
                </TypographyA>

                <TypographyA sx={{ maxWidth: '100%' }}>
                  {(isPipelineVersion
                    ? row.original.gnomadg_af
                    : (row.original.gnomAD_genome_ALL as unknown as string)) ===
                  '.'
                    ? '.'
                    : (isPipelineVersion
                        ? row.original.gnomadg_af
                        : row.original.gnomAD_genome_ALL) === 0
                    ? 0
                    : `${formatScientificToDecimal(
                        isPipelineVersion
                          ? row.original.gnomadg_af
                          : row.original.gnomAD_genome_ALL
                      )}`}
                </TypographyA>

                {isPipelineVersion && (
                  <TypographyA sx={{ maxWidth: '100%' }}>
                    {(row.original.wbbc_af as unknown as string) === '.'
                      ? '.'
                      : row.original.wbbc_af === 0
                      ? 0
                      : `${formatScientificToDecimal(row.original.wbbc_af)}`}
                  </TypographyA>
                )}
              </>
            )}
          </Stack>
        )
      },
      {
        accessorKey: 'f_score',
        id: 'F_score',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Tooltip
              placement="top-start"
              title="F score:GLSofort 软件AI模型综合评分：致病性、质量、表型、遗传模式、文献等等，最可能的致病位点"
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('F')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  Score
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const bgcolor = getBgColor(row)

          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <React.Fragment>
                    <TypographyA
                      onClick={(e) => {
                        e.stopPropagation()
                        props.setGeneOpen(true)
                        props.setCurrent(row.original)
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : bgcolor === 'rgb(107,148,200,0.8)' ||
                              bgcolor === 'rgb(109,56,149,0.8)'
                            ? 'black'
                            : '#23527c',
                        textAlign: 'center',
                        fontWeight: '700'
                      }}
                    >
                      {`${
                        (row.original.f_score as any) !== '.'
                          ? (row.original.f_score * 100)?.toFixed(2)
                          : '.'
                      }`}
                    </TypographyA>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {row.original.group_data
                      ? row.original.group_data
                          .map((item) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)
                            const type = temp ? temp.type : ''

                            return {
                              ...item,
                              type: type,
                              typeIndex:
                                type === 'affected_child'
                                  ? 0
                                  : type === 'mother'
                                  ? 2
                                  : type === 'father'
                                  ? 1
                                  : type === 'child'
                                  ? 0
                                  : 0
                            }
                          })
                          .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
                          .map((item: any) => {
                            if (item && item.f_score) {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <TypographyA
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      props.setGeneOpen(true)
                                      props.setCurrent(row.original)
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      color:
                                        bgcolor === 'rgba(229,38,35,0.8)'
                                          ? 'white'
                                          : bgcolor ===
                                              'rgb(107,148,200,0.8)' ||
                                            bgcolor === 'rgb(109,56,149,0.8)'
                                          ? 'black'
                                          : '#23527c',
                                      textAlign: 'center',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {`${
                                      (item.f_score as any) !== '.'
                                        ? `${(item.f_score * 100)?.toFixed(
                                            2
                                          )}(${
                                            temp
                                              ? judgeType(
                                                  temp.type,
                                                  row.original.group_data
                                                )
                                              : ''
                                          })`
                                        : '.'
                                    }`}
                                  </TypographyA>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}
                  </React.Fragment>
                )}
              </Stack>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'g_score',
        id: 'G_score',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Tooltip
              placement="top-start"
              title={t(
                'G score: Phenotypic correlation score, which is a comprehensive score associated with genetic diseases and other genetic diseases that interact with genes'
              )}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('G')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  Score
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <React.Fragment>
                    <TypographyA
                      sx={{
                        fontSize: '0.8rem',
                        textAlign: 'center',
                        fontWeight: '700'
                      }}
                    >
                      {`${
                        (row.original.g_score as any) !== '.'
                          ? (row.original.g_score * 100)?.toFixed(2)
                          : '.'
                      }`}
                    </TypographyA>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {row.original.group_data
                      ? row.original.group_data
                          .map((item) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)
                            const type = temp ? temp.type : ''

                            return {
                              ...item,
                              type: type,
                              typeIndex:
                                type === 'affected_child'
                                  ? 0
                                  : type === 'mother'
                                  ? 2
                                  : type === 'father'
                                  ? 1
                                  : type === 'child'
                                  ? 0
                                  : 0
                            }
                          })
                          .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
                          .map((item: any) => {
                            if (item && (item.g_score as any) !== '.') {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <TypographyA
                                    sx={{
                                      fontSize: '0.8rem',
                                      color: '#23527c',
                                      textAlign: 'center',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {`${
                                      (item.g_score as any) !== '.'
                                        ? `${(item.g_score * 100)?.toFixed(
                                            2
                                          )}(${
                                            temp
                                              ? judgeType(
                                                  temp.type,
                                                  row.original.group_data
                                                )
                                              : ''
                                          })`
                                        : '.'
                                    }`}
                                  </TypographyA>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}
                  </React.Fragment>
                )}
              </Stack>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'SpliceAI_max_score',
        id: 'SpliceAI_max_score',
        header: () => (
          <Stack maxWidth={'100%'} alignItems="center">
            <Tooltip
              title={`D(${t('harmful')}) U(${t('unknown significance')}) B(${t(
                'benign'
              )})`}
              placement="top-start"
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('SpliceAI')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Revel')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('AlphaM')}
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const spliceAI = getSpliceAI(row.original)
          const revel = getRevel(row.original)
          const amPathogenicity = getAlphaM(row.original)

          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(spliceAI as unknown as string) === '.'
                      ? '.'
                      : `${Number(spliceAI)?.toFixed(2)}`}
                  </span>

                  <span
                    style={{
                      fontWeight: spliceAI >= 0.2 ? 'bold' : 'normal'
                    }}
                  >
                    {`${spliceAI >= 0.2 ? 'D' : spliceAI <= 0.1 ? '' : ''}`}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(revel as unknown as string) === '.'
                      ? '.'
                      : `${Number(revel)?.toFixed(3)}`}
                  </span>

                  <span
                    style={{
                      fontWeight: revel >= 0.644 ? 'bold' : 'normal'
                    }}
                  >
                    {`${revel >= 0.644 ? 'D' : revel <= 0.29 ? '' : ''}`}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(amPathogenicity as unknown as string) === '.' ||
                    !amPathogenicity
                      ? '.'
                      : `${Number(amPathogenicity)?.toFixed(2)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        Number(amPathogenicity) >= 0.564 ? 'bold' : 'normal'
                    }}
                  >
                    {`${
                      Number(amPathogenicity) >= 0.564
                        ? 'D'
                        : Number(amPathogenicity) <= 0.34
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>
              </Stack>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'CLINSIG_final',
        id: 'CLINSIG_final',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Clinvar')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Curation')}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const bgcolor = getBgColor(row)
          const clinvarVariantId = getClinvarVariantId(row.original)
          const clinvarClassification = getClinvarClassification(row.original)
          const clinvarScore = getClinvarScore(row.original)

          return (
            <Stack maxWidth={'100%'} direction={'column'} spacing={1}>
              <Stack
                alignItems={'center'}
                maxWidth={'100%'}
                spacing={1}
                direction="row"
              >
                <Tooltip title={clinvarClassification} placement={'top-start'}>
                  <TypographyA
                    sx={{
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c',
                      cursor: 'pointer',
                      maxWidth: 80,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.variation}/${clinvarVariantId}`
                      )
                    }}
                  >
                    {`${clinvarClassification || ''}`}
                  </TypographyA>
                </Tooltip>

                {clinvarScore && clinvarScore !== '.' ? (
                  <Rating
                    readOnly
                    value={clinvarScore as unknown as number}
                    max={clinvarScore as unknown as number}
                  />
                ) : (
                  ''
                )}
              </Stack>

              <TypographyA
                sx={{
                  maxWidth: '100%'
                }}
              >
                {`${row.original.curation || ''}`}
              </TypographyA>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'ACMG_classification',
        id: 'ACMG_classification',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('ACMG')}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const acmg = row.original.acmg_classification
            ? `${switchACMG(row.original.acmg_classification)}`
            : row.original.user_acmg_classification
            ? `${row.original.user_acmg_classification}`
            : row.original.ACMG_classification
            ? `${row.original.ACMG_classification}`
            : 'VUS'
          let bgcolor =
            color.find((i) => i.key === acmg?.toLowerCase())?.color || 'none'
          if (row.original.user_acmg_classification) {
            bgcolor =
              color.find(
                (i) =>
                  i.key === row.original.user_acmg_classification?.toLowerCase()
              )?.color || 'rgba(0, 0, 0)'
          }
          const acmgScore = getAcmgScore(row.original)

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <Chip
                label={`${formatACMG(acmg)}${acmgScore ? `:${acmgScore}` : ''}`}
                size="small"
                sx={{
                  bgcolor: bgcolor,
                  color: bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
                  fontSize: '0.75rem',
                  fontWeight: 700
                }}
                onClick={() => {
                  props.setAcmgOpen(true)
                  props.setCurrent(row.original)
                }}
              />

              {row.original.user_classification && (
                <Chip
                  label={formatACMG(row.original.user_classification)}
                  size="small"
                  sx={{
                    bgcolor: bgcolor,
                    color:
                      bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
                    fontSize: '0.7rem'
                  }}
                  onClick={() => {
                    props.setCurrent(row.original)
                    props.setOpen(true)
                  }}
                />
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'OMIM',
        id: 'OMIM',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('OMIM')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Inheritance')}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const bgcolor = getBgColor(row)

          // 辅助函数：处理遗传模式数据
          const processInheritanceData = (item: any) => {
            const inheritances: string[] = []
            const clinicalSynopsis = isJson(item.clinicalSynopsis)
              ? JSON.parse(item.clinicalSynopsis)
              : isJson(item.clinical_synopsis)
              ? JSON.parse(item.clinical_synopsis)
              : item.clinicalSynopsis

            if (
              item &&
              clinicalSynopsis &&
              clinicalSynopsis.INHERITANCE &&
              Array.isArray(clinicalSynopsis.INHERITANCE)
            ) {
              clinicalSynopsis.INHERITANCE.forEach((str: string) =>
                inheritances.push(str.toLowerCase())
              )
            }

            return {
              ...item,
              inheritance: formatVariantInheritance(
                Array.from([...new Set(inheritances)]).join(',')
              )
            }
          }

          // 辅助函数：获取OMIM数据
          const getOMIMData = () => {
            if (isPipelineVersion) {
              // Pipeline版本
              if (
                i18n?.language === 'zh' &&
                row.original.omim_curate_phenotypes?.length
              ) {
                return {
                  data: unique(row.original.omim_curate_phenotypes, 'disease'),
                  type: 'curated'
                }
              } else {
                const processedData =
                  row.original?.omim_phenotypes?.map(processInheritanceData) ??
                  []
                return {
                  data: unique(processedData, 'phenoName'),
                  type: 'phenotypes'
                }
              }
            } else {
              // 非Pipeline版本
              if (
                i18n?.language === 'zh' &&
                row.original.OMIM?.geneDiseaseTranslated?.data
              ) {
                return {
                  data: unique(
                    row.original.OMIM.geneDiseaseTranslated.data,
                    'disease'
                  ),
                  type: 'translated'
                }
              } else {
                const processedData =
                  row.original.OMIM?.omimData?.map(processInheritanceData) ?? []
                return {
                  data: unique(processedData, 'phenoName'),
                  type: 'omimData'
                }
              }
            }
          }

          // 辅助函数：渲染OMIM项
          const renderOMIMItem = (item: any, index: number, type: string) => {
            const geneticMode =
              type === 'curated' || type === 'translated'
                ? item.genetic_mode
                : item.inheritance

            const diseaseName =
              type === 'curated' || type === 'translated'
                ? item.disease
                : item.phenoName || ''

            const diseaseNameCn =
              type === 'phenotypes' || type === 'omimData'
                ? item.pheno_name_cn
                : ''

            return (
              <TypographyA
                sx={{
                  width: '100%',
                  maxWidth: 200,
                  lineHeight: '24px',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  color:
                    bgcolor === 'rgb(109,56,149,0.8)' ||
                    bgcolor === 'rgb(107,148,200,0.8)' ||
                    bgcolor === 'rgba(229,38,35,0.8)'
                      ? 'white'
                      : '#23527c',
                  mb: '4px'
                }}
                onClick={() => {
                  props.setCurrent(row.original)
                  props.setOMIMOpen(true)
                }}
                key={`${
                  type === 'curated' || type === 'translated'
                    ? item.disease
                    : item.phenoMim
                }_${index}`}
              >
                <Chip
                  label={`${geneticMode ? geneticMode : '.'}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.75rem',
                    fontWeight: '700'
                  }}
                />

                <span style={{ lineHeight: '24px' }}>
                  {i18n?.language === 'zh' && diseaseNameCn
                    ? `${diseaseNameCn}-${diseaseName}`
                    : diseaseName}
                </span>
              </TypographyA>
            )
          }

          // 辅助函数：渲染Tooltip内容
          const renderTooltipContent = (OMIMValue: any[], type: string) => (
            <React.Fragment>
              {OMIMValue.map((item: any, index: any) => {
                const geneticMode =
                  type === 'curated' || type === 'translated'
                    ? item.genetic_mode
                    : item.inheritance
                    ? item.inheritance
                    : ''

                const diseaseName =
                  type === 'curated' || type === 'translated'
                    ? item.disease
                    : item.phenoName || ''

                const diseaseNameCn =
                  type === 'phenotypes' || type === 'omimData'
                    ? item.pheno_name_cn
                    : ''

                return (
                  <TypographyA
                    sx={{
                      fontSize: '0.85rem',
                      lineHeight: '1rem',
                      maxWidth: isWindow()
                        ? '400px !important'
                        : '200px !important'
                    }}
                    key={`${
                      type === 'curated' || type === 'translated'
                        ? item.disease
                        : item.phenoName
                    }_${index}`}
                  >
                    {`(${geneticMode ? geneticMode : '.'})-${
                      i18n?.language === 'zh' && diseaseNameCn
                        ? `${diseaseNameCn}-`
                        : ''
                    }${diseaseName}`}
                  </TypographyA>
                )
              })}
            </React.Fragment>
          )

          // 主逻辑
          const { data: OMIMValue, type } = getOMIMData()

          return (
            <Stack maxWidth={'100%'} spacing={1}>
              {OMIMValue && OMIMValue.length ? (
                <HtmlTooltip
                  title={renderTooltipContent(OMIMValue, type)}
                  placement={'top-start'}
                  TransitionComponent={Slide}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: isWindow() ? '400px' : '200px'
                    }
                  }}
                >
                  <Box>
                    {OMIMValue.slice(0, 2).map((items: any, index: any) =>
                      renderOMIMItem(items, index, type)
                    )}
                  </Box>
                </HtmlTooltip>
              ) : (
                <TypographyA>.</TypographyA>
              )}
            </Stack>
          )
        }
      }
    ]

    if (!props.indexChecked) {
      column = column.filter((i) => i.id !== 'index')
    }

    return column
  }, [props, user, props.indexChecked, isPipelineVersion])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default Columns1
